/**
 * Structured-control-flow recovery.
 *
 * `structureByGoto` (the correctness backstop) emits a flat sequence of `label`/body/`goto`
 * per basic block whenever ANY region of a function is unstructurable — so even the reducible
 * parts lose structure. This pass runs over that flat sequence and recovers readable control
 * flow where the goto graph is in fact reducible, leaving explicit gotos only for the
 * genuinely-irreducible remainder.
 *
 * It is a no-op for any function with no `label` nodes (the structured majority), so their
 * output is unchanged.
 *
 * Phase 1 (this file): reachability cleanup — drop segments no reachable goto/fall-through can
 * reach, drop a `goto` that lands on the immediately-following label, and drop label markers
 * that no surviving goto targets.
 */

import { Statement, LoweredNode } from './ExpressionIR';
import { negateExpression } from './LoweredTransforms';

export function recoverStructuredControlFlow(node: LoweredNode): LoweredNode {
  if (!containsLabel(node)) {
    return node;
  }
  // Each rewrite (cleanup, forward-if) removes labels/gotos and can expose another; iterate
  // to a fixpoint, bounded by the strictly-decreasing label+goto count.
  let current = node;
  let markerCount = countMarkers(current);
  for (let iteration = 0; iteration < markerCount + 1; iteration++) {
    current = mapSequences(current, sequence => recoverSequence(sequence));
    const next = countMarkers(current);
    if (next === markerCount) {
      break;
    }
    markerCount = next;
  }
  return current;
}

function recoverSequence(sequence: LoweredNode & { kind: 'sequence' }): LoweredNode {
  const cleaned = cleanupGotoSequence(sequence);
  if (cleaned.kind !== 'sequence') {
    return cleaned;
  }
  return recoverForwardIfs(cleaned);
}

function countMarkers(node: LoweredNode): number {
  let count = 0;
  forEachNode(node, child => {
    if (child.kind === 'label' || child.kind === 'goto') {
      count++;
    }
  });
  return count;
}

/**
 * Recovers `if (cond) { goto L; } <body>; L:` into `if (!cond) { <body> }` when L is targeted
 * only by that one goto and the body in between holds no jump target. Semantically the body
 * runs exactly when the branch is not taken, so inverting the condition is faithful.
 */
function recoverForwardIfs(sequence: LoweredNode & { kind: 'sequence' }): LoweredNode {
  const children = sequence.children;
  const targetCounts = gotoTargetCounts(children);
  for (let index = 0; index < children.length; index++) {
    const target = guardGotoTarget(children[index]);
    if (!target || targetCounts.get(target) !== 1) {
      continue;
    }
    const labelIndex = children.findIndex((child, after) => after > index && child.kind === 'label' && child.name === target);
    if (labelIndex < 0) {
      continue;
    }
    const between = children.slice(index + 1, labelIndex);
    if (between.some(child => containsLabel(child))) {
      continue;
    }
    const guard = children[index] as LoweredNode & { kind: 'if' };
    const invertedIf: LoweredNode = { kind: 'if', condition: negateExpression(guard.condition), thenBody: { kind: 'sequence', children: between }, elseBody: null };
    return { kind: 'sequence', children: [...children.slice(0, index), invertedIf, ...children.slice(labelIndex + 1)] };
  }
  return sequence;
}

// The label an `if (cond) { goto L; }` (no else) jumps to, or null when the node is not that shape.
function guardGotoTarget(node: LoweredNode): string | null {
  if (node.kind !== 'if' || node.elseBody !== null) {
    return null;
  }
  return soleGotoTarget(node.thenBody);
}

// The target of a node that is exactly one `goto` (possibly wrapped in a single-child
// sequence/block), or null.
function soleGotoTarget(node: LoweredNode): string | null {
  if (node.kind === 'goto') {
    return node.target;
  }
  if (node.kind === 'sequence' && node.children.length === 1) {
    return soleGotoTarget(node.children[0]);
  }
  return null;
}

function gotoTargetCounts(children: LoweredNode[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const node of children) {
    forEachNode(node, child => {
      if (child.kind === 'goto') {
        counts.set(child.target, (counts.get(child.target) ?? 0) + 1);
      }
    });
  }
  return counts;
}

function containsLabel(node: LoweredNode): boolean {
  let found = false;
  forEachNode(node, child => {
    if (child.kind === 'label') {
      found = true;
    }
  });
  return found;
}

/** A label and the nodes that follow it up to the next label — one basic block. */
interface Segment {
  label: string;
  body: LoweredNode[];
}

// Rewrites a goto sequence: removes unreachable segments (label-led fallback shape only),
// then drops redundant gotos and label markers that nothing targets. The label cleanup runs
// on any sequence so it still applies after an earlier pass left the list not label-led.
function cleanupGotoSequence(sequence: LoweredNode & { kind: 'sequence' }): LoweredNode {
  let children = sequence.children;
  const segments = splitIntoSegments(children);
  if (segments) {
    const reachable = reachableSegments(segments);
    children = segments.flatMap((segment, index) =>
      reachable.has(index) ? [{ kind: 'label', name: segment.label } as LoweredNode, ...segment.body] : []);
  }
  // Drop a `goto X` that lands on the very next label first, so a label whose only reference
  // was that goto is then seen as unreferenced and removed too.
  children = dropRedundantGotos(children);
  const targeted = gotoTargets(children);
  children = children.filter(child => child.kind !== 'label' || targeted.has(child.name));
  return { kind: 'sequence', children };
}

// Splits a flat child list into label-led segments. Returns null when the list is not in the
// goto-fallback shape (does not start with a label), leaving it for normal structuring.
function splitIntoSegments(children: LoweredNode[]): Segment[] | null {
  if (children.length === 0 || children[0].kind !== 'label') {
    return null;
  }
  const segments: Segment[] = [];
  for (const child of children) {
    if (child.kind === 'label') {
      segments.push({ label: child.name, body: [] });
    } else {
      segments[segments.length - 1].body.push(child);
    }
  }
  return segments;
}

// Breadth-first reachability over segments: segment 0 is the entry, a segment reaches the next
// one when its body can fall through, and reaches any segment a goto in its body targets.
function reachableSegments(segments: Segment[]): Set<number> {
  const indexByLabel = new Map<string, number>();
  segments.forEach((segment, index) => indexByLabel.set(segment.label, index));
  const reachable = new Set<number>();
  const worklist = [0];
  while (worklist.length > 0) {
    const index = worklist.pop()!;
    if (reachable.has(index) || index >= segments.length) {
      continue;
    }
    reachable.add(index);
    const segment = segments[index];
    if (!bodyTerminates(segment.body)) {
      worklist.push(index + 1);
    }
    for (const target of gotoTargets(segment.body)) {
      const targetIndex = indexByLabel.get(target);
      if (targetIndex !== undefined) {
        worklist.push(targetIndex);
      }
    }
  }
  return reachable;
}

// Drops a `goto X` whose immediately-following sibling is `X:` (the jump is a fall-through).
function dropRedundantGotos(children: LoweredNode[]): LoweredNode[] {
  return children.filter((child, index) => {
    const next = children[index + 1];
    return !(child.kind === 'goto' && next && next.kind === 'label' && next.name === child.target);
  });
}

// ─── control-flow predicates ───

function bodyTerminates(body: LoweredNode[]): boolean {
  return body.length > 0 && nodeTerminates(body[body.length - 1]);
}

function nodeTerminates(node: LoweredNode): boolean {
  switch (node.kind) {
    case 'return': case 'unreachable': case 'break': case 'continue':
    case 'labeled_break': case 'labeled_continue': case 'goto':
      return true;
    case 'sequence':
      return node.children.length > 0 && nodeTerminates(node.children[node.children.length - 1]);
    case 'block':
      return node.body.length > 0 && statementTerminates(node.body[node.body.length - 1]);
    case 'if':
      return node.elseBody !== null && nodeTerminates(node.thenBody) && nodeTerminates(node.elseBody);
    default:
      return false;
  }
}

function statementTerminates(statement: Statement): boolean {
  return statement.kind === 'return' || statement.kind === 'unreachable';
}

function gotoTargets(body: LoweredNode[]): Set<string> {
  const targets = new Set<string>();
  for (const node of body) {
    forEachNode(node, child => {
      if (child.kind === 'goto') {
        targets.add(child.target);
      }
    });
  }
  return targets;
}

// ─── generic tree walks ───

function forEachNode(node: LoweredNode, visit: (node: LoweredNode) => void): void {
  visit(node);
  for (const child of childNodes(node)) {
    forEachNode(child, visit);
  }
}

function mapSequences(node: LoweredNode, transform: (sequence: LoweredNode & { kind: 'sequence' }) => LoweredNode): LoweredNode {
  const mapped = mapChildNodes(node, child => mapSequences(child, transform));
  return mapped.kind === 'sequence' ? transform(mapped) : mapped;
}

function childNodes(node: LoweredNode): LoweredNode[] {
  switch (node.kind) {
    case 'sequence': return node.children;
    case 'if': return node.elseBody ? [node.thenBody, node.elseBody] : [node.thenBody];
    case 'while': case 'do_while': case 'for': case 'labeled_block': return [node.body];
    case 'switch': return [...node.cases.map(caseEntry => caseEntry.body), node.defaultBody];
    default: return [];
  }
}

function mapChildNodes(node: LoweredNode, transform: (child: LoweredNode) => LoweredNode): LoweredNode {
  switch (node.kind) {
    case 'sequence':
      return { kind: 'sequence', children: node.children.map(transform) };
    case 'if':
      return { kind: 'if', condition: node.condition, thenBody: transform(node.thenBody), elseBody: node.elseBody ? transform(node.elseBody) : null };
    case 'while':
      return { kind: 'while', condition: node.condition, body: transform(node.body) };
    case 'do_while':
      return { kind: 'do_while', body: transform(node.body), condition: node.condition };
    case 'for':
      return { kind: 'for', init: node.init, condition: node.condition, increment: node.increment, body: transform(node.body) };
    case 'labeled_block':
      return { kind: 'labeled_block', label: node.label, body: transform(node.body) };
    case 'switch':
      return { kind: 'switch', selector: node.selector, cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: transform(caseEntry.body) })), defaultBody: transform(node.defaultBody) };
    default:
      return node;
  }
}
